import React, { useState, useEffect } from 'react';
import { useFlowNavigation, useFlow } from '../../../contexts/FlowContext';
import { useSession } from '../../../contexts/SessionContext';
import { useTheme } from '../../../contexts/ThemeContext';
import PageLayout from '../../../components/PageLayout/CarelonRx';
import UsernameService from '../../../services/username/UsernameService';
import RateLimiter from '../../../security/RateLimiter';
import SessionExpiryModal from '../../../components/modals/SessionExpiryModal';
import SecureSessionService from '../../../services/session/SecureSessionService';
import { ORIGINAL_CLIENT_REDIRECT_URL } from '../../../config';
import { getLogoutRedirectUrl } from '../../../utils/nativeApp';
import '../../../styles/CarelonRx/login.css';
import '../../../styles/CarelonRx/updateUsername.css';

const UsernameInputPage: React.FC = () => {
  const { nextStep, goToStep } = useFlowNavigation();
  const { setFlowData, getFlowData, resetFlow } = useFlow();
  const { sessionData } = useSession();
  const { theme } = useTheme();
  const isNativeApp = getFlowData('device') === 'native_app';
  
  const [currentEmail, setCurrentEmail] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [useEmailAsUsername, setUseEmailAsUsername] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [apiError, setApiError] = useState('');
  const [usernameExistsError, setUsernameExistsError] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [showRequiredError, setShowRequiredError] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [usernameSuggestions, setUsernameSuggestions] = useState<string[]>([]);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const contactDetails = getFlowData('contactDetails');
  const selectedEmailId = getFlowData('selectedEmailId');
  const messages = (theme as any).messages?.ui?.usernameChange || {};
  const validationMessages = (theme as any).messages?.errors?.validation || {};

  useEffect(() => {
    // Re-sync React Context expiresAt from sessionStorage on mount (covers back navigation).
    // sessionId is NOT touched — only the timer is refreshed.
    const storedSession = SecureSessionService.getStoredSession();
    if (storedSession?.expiresAt) {
      window.dispatchEvent(new CustomEvent('session-refreshed', { detail: { expiresAt: storedSession.expiresAt } }));
    }
  }, []);

  useEffect(() => {
    if (contactDetails && selectedEmailId) {
      const selectedEmail = contactDetails.emails?.find((e: any) => e.id === selectedEmailId);
      if (selectedEmail) {
        const fullEmail = selectedEmail.fullEmail || selectedEmail.maskedEmail || '';
        setCurrentEmail(fullEmail);
      }
    }
  }, [contactDetails, selectedEmailId]);

  useEffect(() => {
    if (useEmailAsUsername) {
      setNewUsername(currentEmail);
    } else {
      setNewUsername('');
    }
  }, [useEmailAsUsername, currentEmail]);

  useEffect(() => {
    if (isFocused || hasStartedTyping) {
      if (newUsername) {
        validateUsernamePolicy(newUsername);
        setUsernameExistsError('');
        setShowRequiredError(false);
      } else {
        // Show all validation rules when field is empty but focused
        setValidationErrors([
          'Use at least 6 characters.',
          "Don't use spaces.",
          "Don't begin or end with . (dot)"
        ]);
        setUsernameExistsError('');
      }
    } else {
      setValidationErrors([]);
      setShowRequiredError(false);
    }
  }, [newUsername, hasStartedTyping, isFocused]);

  const validateUsernamePolicy = (username: string) => {
    const errors: string[] = [];

    if (username.length < 6) {
      errors.push('Use at least 6 characters.');
    }

    if (/\s/.test(username)) {
      errors.push("Don't use spaces.");
    }

    if (/^\./.test(username) || /\.$/.test(username)) {
      errors.push("Don't begin or end with . (dot)");
    }

    setValidationErrors(errors);
  };

  const handleBack = () => {
    const factorType = getFlowData('factorType');

    if (factorType === 'EMAIL') {
      // User verified via email — TextVoice page was never shown
      goToStep('mfa-method');
    } else {
      // User verified via SMS or CALL — TextVoice page was in the forward path
      goToStep('mfa-phone-delivery');
    }
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = () => {
    SecureSessionService.clearSession();
    const redirectUrl = isNativeApp
      ? getLogoutRedirectUrl()
      : ORIGINAL_CLIENT_REDIRECT_URL;
    window.location.href = redirectUrl;
  };

  const dismissCancelModal = () => {
    setShowCancelModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setApiError('');
    setUsernameExistsError('');
    setUsernameSuggestions([]);
    setIsSubmitting(true);

    try {
      if (!sessionData?.sessionId) {
        throw new Error('No valid session found');
      }

      const userIdentifier = sessionData.userId || 'anonymous';
      const rateLimitResult = RateLimiter.isAllowed(`username_update_${userIdentifier}`, 5, 300000);
      
      if (!rateLimitResult.allowed) {
        setApiError('Too many attempts. Please wait 5 minutes before trying again.');
        setIsSubmitting(false);
        return;
      }

      const response = await UsernameService.updateUsername({
        newUsername: newUsername
      });

      if (response.success) {
        setFlowData('newUsername', newUsername);
        nextStep();
      } else {
        setError(response.message || 'Failed to update username');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      const statusCode = err.response?.status;
      const suggestions = err.response?.data?.suggestions || [];

      if (statusCode === 409 || errorMessage?.toLowerCase().includes('already exists') || errorMessage?.toLowerCase().includes('already taken')) {
        setUsernameExistsError("Looks like there's a problem with the username you entered. It could be one of the following reasons:");
        if (suggestions && suggestions.length > 0) {
          setUsernameSuggestions(suggestions);
        }
      } else if (statusCode === 400) {
        setUsernameExistsError(validationMessages.usernameFormat || 'Invalid username format. Please try again.');
      } else if (statusCode === 404 || statusCode >= 500) {
        setApiError("Sorry, looks like something isn't working. Please give us time to fix the issue. Then, try again.");
      } else if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') {
        setApiError("Sorry, looks like something isn't working. Please give us time to fix the issue. Then, try again.");
      } else {
        setApiError("Sorry, looks like something isn't working. Please give us time to fix the issue. Then, try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = newUsername.trim().length > 0 && validationErrors.length === 0 && !isSubmitting;

  return (
    <PageLayout bannerTitle="Update Username">
      <div className="login-page-container">
        <div className="login-left-panel">
          {!isNativeApp && (
            <a className="usernameupdate-back-link" onClick={handleBack}>
              <img src="/assets/carelonRx/images/Keyboard Arrow Left.svg" alt="" />
              <span>Back</span>
            </a>
          )}

          <h1 className="usernameupdate-title">
            {messages.title || 'To help keep your account secure, please update your username.'}
          </h1>

          <p className="update-username-description">
            {messages.description || "We've updated our security. Please create a new, unique username to continue."}
          </p>

          <form onSubmit={handleSubmit} className="update-username-form">
            {currentEmail && (
              <>
                <div className="form-group">
                  <label htmlFor="currentEmail" className="form-label">
                    {messages.emailLabel || 'Email'}
                  </label>
                  <input
                    id="currentEmail"
                    type="text"
                    value={currentEmail}
                    className="form-input disabled"
                    readOnly
                    disabled
                  />
                  <p className="helper-text">
                    {messages.emailHelper || 'If this email is incorrect, you can update it in your Profile after you have logged in.'}
                  </p>
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={useEmailAsUsername}
                      onChange={(e) => setUseEmailAsUsername(e.target.checked)}
                    />
                    <span className="checkbox-text">{messages.checkboxLabel || 'Use my email address as my username.'}</span>
                  </label>
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="newUsername" className={`form-label ${((isTouched && !newUsername.trim()) || (hasStartedTyping && validationErrors.length > 0) || usernameExistsError) ? 'error' : ''}`}>
                {messages.newUsernameLabel || 'New Username'}
              </label>
              <input
                id="newUsername"
                type="text"
                value={newUsername}
                placeholder="Enter new username"
                onFocus={() => {
                  setIsFocused(true);
                  if (!hasStartedTyping) {
                    setHasStartedTyping(true);
                  }
                  // Keep required error visible if field is empty and was touched
                  if (isTouched && !newUsername.trim()) {
                    setShowRequiredError(true);
                  }
                }}
                onChange={(e) => {
                  setNewUsername(e.target.value);
                  if (!hasStartedTyping) {
                    setHasStartedTyping(true);
                  }
                  if (isTouched && e.target.value.trim() === '') {
                    setShowRequiredError(true);
                  } else {
                    setShowRequiredError(false);
                  }
                }}
                onBlur={() => {
                  setIsFocused(false);
                  setIsTouched(true);
                  if (!newUsername.trim()) {
                    setShowRequiredError(true);
                  } else {
                    setShowRequiredError(false);
                  }
                }}
                className={`form-input ${((isTouched && !newUsername.trim()) || (hasStartedTyping && validationErrors.length > 0) || usernameExistsError) ? 'error' : ''}`}
                disabled={isSubmitting}
                required
              />
              <div className="username-errors">
                {(isTouched && !newUsername.trim() && !isFocused) && (
                  <div className="crx-error-container">
                    <img src="/assets/carelonRx/images/info_error.svg" alt="" className="error-icon" />
                    <span className="crx-error-text">Required field.</span>
                  </div>
                )}
                {(isFocused || hasStartedTyping) && validationErrors.length > 0 && (
                  <div className="validation-errors">
                    {validationErrors.map((err, index) => (
                      <div key={index} className="crx-error-container">
                        <img src="/assets/carelonRx/images/info_error.svg" alt="" className="error-icon" />
                        <span className="crx-error-text">{err}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {usernameExistsError && (
                <div className="username-exists-error">
                  <div className="error-message-with-icon">
                    <img 
                      src="/assets/carelonRx/images/infoIconRed.svg" 
                      alt="Error" 
                      className="error-icon"
                    />
                    <div className="error-text">
                      <p>{usernameExistsError}</p>
                      <ul className="error-reasons">
                        <li>The username is not allowed by our system.</li>
                        <li>The username does not meet the required format.</li>
                      </ul>
                    </div>
                  </div>
                  {usernameSuggestions.length > 0 && (
                    <div className="username-suggestions">
                      <p className="suggestions-title">Here are some alternative username suggestions:</p>
                      <p className="suggestions-list">{usernameSuggestions.join(', ')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {apiError && (
              <div className="api-error-banner">
                <img 
                  src="/assets/carelonRx/images/Error Outline.svg" 
                  alt="Error" 
                  className="error-icon"
                />
                <span>{apiError}</span>
              </div>
            )}
          </form>
        </div>

        <div className="mfa-footer">
          <div className="mfa-footer-buttons">
            <button
              type="button"
              className="crx-button crx-button-secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {messages.cancelButton || 'Cancel'}
            </button>
            <button
              type="button"
              className="crx-button crx-button-primary"
              onClick={handleSubmit}
              disabled={!isFormValid}
            >
              {isSubmitting ? 'Updating...' : (messages.submitButton || 'Update')}
            </button>
          </div>
        </div>
      </div>
      <SessionExpiryModal />
      
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">Important: Action Required to Log In</h2>
            <p className="modal-message">
              You must create a new, unique username to log in.
            </p>
            <div className="modal-actions">
              <button onClick={dismissCancelModal} className="modal-btn-cancel">
                No, Don't Cancel
              </button>
              <button onClick={confirmCancel} className="modal-btn-stay">
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default UsernameInputPage;
